import axios from "axios";

import {
  getJwtToken,
  getRefreshToken,
} from "@/components/session/JwtTokenManager";

/**
 * LLM-related utility functions
 */

/**
 * Generate a concise conversation title based on the first message using LLM
 * @param message The first message in the conversation
 * @returns Promise<string> The generated title or fallback
 */
export async function generateConversationTitle(
  message: string,
): Promise<string> {
  try {
    const response = await axios.post(
      `${API_HOST}/llm/complete`,
      {
        message: `You are tasked with generating a concise, professional conversation title based on the first message of a conversation. This title should be suitable for presentation to corporate bosses and colleagues.

Follow these guidelines when creating the title:
1. Keep it brief, ideally 3-7 words
2. Make it descriptive and relevant to the main topic of the message
3. Use professional language and avoid colloquialisms
4. Ensure it's clear and easily understandable
5. If appropriate, include any key entities, products, or concepts mentioned

Here is the first message of the conversation:

<first_message>
"${message}"
</first_message>

To generate the title:
1. Read the message carefully
2. Identify the main topic or purpose of the message
3. Extract key words or phrases that best represent the content
4. Formulate a concise title that captures the essence of the message
5. Ensure the title adheres to the guidelines mentioned above

Present engaging title (max 6 words) for a conversation that starts with this message. Only return the title, no quotes or extra text.`,
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `JWT ${getJwtToken()}`,
          Session: getRefreshToken() ?? "",
        },
      },
    );

    const data = response.data;

    if (data.response) {
      // Clean up the response - remove quotes and trim
      const title = data.response.replace(/^["']|["']$/g, "").trim();

      // Ensure title isn't too long
      if (title.length > 50) {
        return title.substring(0, 47) + "...";
      }

      return title;
    }

    throw new Error("No response from LLM");
  } catch (error) {
    console.error("Error generating conversation title:", error);

    // Fallback to truncated first message
    const fallbackTitle =
      message.length > 30 ? message.substring(0, 27) + "..." : message;

    return fallbackTitle || "New Conversation";
  }
}

/**
 * Generate collection details (emoji, short description, overview) based on training scenarios using LLM
 * @param scenarios Array of training scenarios to analyze
 * @returns Promise<object | null> Object containing emoji, shortDesc, and overview, or null if generation fails
 */
export async function generateCollectionDetails(
  scenarios: Array<unknown>,
): Promise<{
  emoji: string;
  shortDesc: string;
  overview: string;
} | null> {
  try {
    const response = await axios.post(
      `${API_HOST}/llm/complete`,
      {
        message: `# Collection Description Generator Instructions
You will be provided with a collection of training scenarios. Your task is to analyze them and output exactly 3 things in this format:
## Input Analysis
Read through all provided scenarios and identify:
- **Core skill/topic** being practiced (e.g., mentorship, leadership, sales, feedback)
- **Common workplace context** (e.g., hybrid work, financial services, healthcare)
- **Key challenges** addressed across scenarios (3-4 main ones)
- **Primary skills developed** (3-4 specific competencies)
- **Target audience** and experience level
## Required Output Format
OUTPUT ONLY A JSON with these three fields: emoji, shortDesc, overview

Example output:
{
  "emoji": "1f389",
  "shortDesc": "Master challenging feedback conversations through realistic workplace scenarios that build your confidence.",
  "overview": "**Overview**\nDelivering effective feedback is crucial in today's workplace. These scenarios help you practice navigating sensitive conversations, turning awkward moments into growth opportunities.\n\n**What You'll Practice:**\n* Addressing performance issues\n* Delivering criticism constructively\n* Handling defensive reactions\n* Building psychological safety\n\n**Skills You'll Develop:**\n* Active listening\n* SBI feedback framework\n* Creating safe environments\n* Turning difficult conversations into opportunities"
}

**1. EMOJI:**
[Single Unicode emoji that represents growth, development, or the core skill]
**2. SHORT DESCRIPTION:**
Master challenging [SKILL] conversations through realistic workplace scenarios that build your confidence in [PRIMARY BENEFIT].
**3. LONG DESCRIPTION:**
**Overview**
[2-3 sentences that can vary in structure but should cover: why this skill matters, what makes it challenging in today's workplace, how these scenarios help, and the practical value/immediate application aspect]
**What You'll Practice:**
* [Challenge 1 from scenarios]
* [Challenge 2 from scenarios]
* [Challenge 3 from scenarios]
* [Challenge 4 from scenarios]
**Skills You'll Develop:**
* [Skill 1 - communication related]
* [Skill 2 - framework/methodology related]
* [Skill 3 - relationship/environment related]
* [Skill 4 - strategic/outcome related]
## Overview Variations
The overview can take different approaches:
- **Challenge-focused**: "X skill is harder than ever because of [modern challenge]. These scenarios..."
- **Growth-focused**: "Building strong X skills requires practice in real situations. These scenarios..."
- **Problem-solution**: "Every leader faces X challenges. These scenarios give you..."
- **Outcome-focused**: "The best X professionals know how to [key skill]. These scenarios help you..."
## Guidelines
- Keep language simple and conversational
- Focus on practical application and immediate value
- Use active voice and confident tone
- Short description should be one sentence, under 20 words
- Each bullet point should be specific and actionable
- Match the professional but approachable tone of the example
- Vary the overview structure to fit the content naturally
- REPLY ONLY WITH THE JSON. DON'T SHOW THE ANALYSIS.

# Provided Scenarios:
${JSON.stringify(scenarios)}
`,
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `JWT ${getJwtToken()}`,
          Session: getRefreshToken() ?? "",
        },
        timeout: 30000, // 30 seconds
      },
    );

    const data = response.data;

    return JSON.parse(data.response);
  } catch (error) {
    console.error("Error generating collection details:", error);

    return null;
  }
}

/**
 * Generate an audio introduction script for a training scenario roleplay using LLM
 * @param scenario Training scenario object containing character details, context, and objectives
 * @returns Promise<string | null> The generated audio introduction script, or null if generation fails
 */
export async function generateIntroductionAudio(scenario: {
  shortDesc: string;
  context: string;
  objective: string;
  name: string;
  persona: {
    name: string;
    firstName: string;
    lastName: string;
    personalityTraits: {
      [key: string]: string;
    };
    personalityCharacteristics?: string[];
    jobTitle?: string;
    company?: string;
    backstory?: string;
    situationalUnderstanding?: string;
  };
}): Promise<string | null> {
  try {
    const response = await axios.post(
      `${API_HOST}/llm/complete`,
      {
        message: `Audio Introduction Generator Instructions

You will be provided with a single training scenario. Your task is to analyze it and create a brief audio introduction that will play before the roleplay begins.

Input Analysis

<scenario>
${JSON.stringify(scenario)}
</scenario>

Read through the provided scenario and identify:

Main character (name and role)

Key challenge the character is facing

Primary objective for the participant

Secondary considerations or constraints

Important context the participant should understand

Required Output Format

OUTPUT ONLY A JSON with this single field: audio_intro

Example output:
{
  "audio_intro": "Welcome to Roleplays. In this roleplay, you'll be delivering difficult news to Scott Walker, a Senior Operations Manager who is unaware of the company's full financial crisis. Take a look at the scenario context in front of you to understand Scott's specific background and expectations. Your goal is to communicate the company closure with transparency and empathy while ensuring employees understand the business rationale and receive clear information about their final compensation. You'll hear a ding and the next voice you'll hear is Scott's. Good luck!",
}


AUDIO_INTRO: Welcome to Roleplays. In this roleplay, you'll be [ACTION] with [CHARACTER NAME], a [ROLE] who [KEY CHALLENGE]. Take a look at the scenario context in front of you to understand [CHARACTER NAME]'s specific [SITUATION/CONCERNS/BACKGROUND]. Your goal is to [PRIMARY OBJECTIVE] while [SECONDARY CONSIDERATION]. You'll hear a ding and the next voice you'll hear is [CHARACTER NAME]'s. Good luck!

Template Variables Guide

[ACTION]: What the participant will be doing (e.g., "having a feedback conversation", "conducting a performance review", "leading a team meeting")

[CHARACTER NAME]: The name of the person they'll be speaking with

[ROLE]: The character's job title or position

[KEY CHALLENGE]: The main issue or situation the character is dealing with (e.g., "is struggling with remote team communication", "has concerns about a new process")

[SITUATION/CONCERNS/BACKGROUND]: Choose the most appropriate word based on context

[PRIMARY OBJECTIVE]: The main goal the participant should achieve

[SECONDARY CONSIDERATION]: Important constraints or additional goals (often starts with "while maintaining...", "while being sensitive to...", "while ensuring...")

Guidelines

Keep the introduction under 30 seconds when spoken

Use natural, conversational language

Be specific about the character's challenge to set proper context

Make the objectives clear and actionable

Use the character's name twice for familiarity

Choose action verbs that match the scenario type

REPLY ONLY WITH THE JSON. DON'T SHOW THE ANALYSIS.`,
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `JWT ${getJwtToken()}`,
          Session: getRefreshToken() ?? "",
        },
      },
    );

    const data = response.data;

    return JSON.parse(data.response).audio_intro;
  } catch (error) {
    console.error("Error generating audio introduction:", error);

    return null;
  }
}
